import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/auth";
import { CheckCircle, XCircle, Loader2, LogIn } from "lucide-react";

interface AcceptInviteFlowProps {
  /** The path component used in the invite URL (e.g. "/accept-invite") */
  returnPath: string;
  /** Title shown when the user is not authenticated */
  title: string;
  /** Subtitle shown when the user is not authenticated */
  subtitle: string;
  /** The API function to call with the token */
  acceptFn: (token: string) => Promise<unknown>;
  /** Called on successful accept — receives the API response */
  onSuccess: (data: unknown) => void;
  /** Called on error. Return true if the invite was already accepted. */
  onError: () => Promise<boolean>;
  /** Success screen message */
  successMessage: string;
  /** Where to link after success */
  successLink: string;
  /** Label for the success link */
  successLabel: string;
}

export function AcceptInviteFlow({
  returnPath,
  title,
  subtitle,
  acceptFn,
  onSuccess,
  onError,
  successMessage,
  successLink,
  successLabel,
}: AcceptInviteFlowProps) {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { isAuthenticated, isLoading } = useAuthStore();
  const [alreadyAccepted, setAlreadyAccepted] = useState(false);

  const mutation = useMutation({
    mutationFn: () => acceptFn(token!),
    onSuccess: (data) => onSuccess(data),
    onError: async () => {
      const wasAccepted = await onError();
      if (wasAccepted) setAlreadyAccepted(true);
    },
  });

  useEffect(() => {
    if (token && isAuthenticated && !isLoading) {
      mutation.mutate();
    }
  }, [token, isAuthenticated, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!token) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <XCircle className="h-10 w-10 text-bad mb-4" />
        <p className="text-ink font-semibold mb-2">Invalid Link</p>
        <p className="text-ink-3 text-sm">No invite token found.</p>
      </div>
    );
  }

  if (!isLoading && !isAuthenticated) {
    const returnTo = encodeURIComponent(`${returnPath}?token=${token}`);
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <LogIn className="h-10 w-10 text-ink-3 mb-4" />
        <p className="text-ink font-semibold mb-2">{title}</p>
        <p className="text-ink-3 text-sm mb-6">{subtitle}</p>
        <div className="flex gap-3">
          <Link
            to={`/login?returnTo=${returnTo}`}
            className="btn btn--ghost"
          >
            Sign in
          </Link>
          <Link
            to={`/register?returnTo=${returnTo}`}
            className="btn btn--accent"
          >
            Create account
          </Link>
        </div>
      </div>
    );
  }

  if (isLoading || mutation.isPending) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-ink-3">
        <Loader2 className="h-8 w-8 animate-spin mb-4" />
        <p>Accepting invite...</p>
      </div>
    );
  }

  if (alreadyAccepted || mutation.isSuccess) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <CheckCircle className="h-10 w-10 text-ok mb-4" />
        <p className="text-ink font-semibold mb-2">Invite Accepted</p>
        <p className="text-ink-3 text-sm mb-4">{successMessage}</p>
        <Link
          to={successLink}
          className="btn btn--ghost"
        >
          {successLabel}
        </Link>
      </div>
    );
  }

  if (mutation.isError) {
    const errorMessage =
      mutation.error instanceof Error
        ? mutation.error.message
        : "Invalid or expired invite link.";
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <XCircle className="h-10 w-10 text-bad mb-4" />
        <p className="text-ink font-semibold mb-2">Invite Failed</p>
        <p className="text-ink-3 text-sm mb-4">{errorMessage}</p>
        <Link to="/dashboard" className="text-accent hover:underline text-sm">
          Go to Dashboard
        </Link>
      </div>
    );
  }

  return null;
}
